/**
 * ManagerReadOnlyMenu — mobile menu for role=manager (Regional Manager).
 *
 * S5 Addendum: oversight-only. Read-only access to Fleet Status, Team
 * Assignments, Service History Summary, Updates & Notices, and
 * Messages. No operational controls (no scan, no execution, no
 * Compliance Trophy, no Operation Record).
 *
 * Existing role gating on the individual components already treats
 * `manager` as an oversight audience — we simply mount them here in a
 * single-column mobile layout with a tabbed bottom nav.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, MessageSquare, Truck, Users, Sparkles } from 'lucide-react';
import { MobileShell, BottomNavButton } from './MobileShell';

// Existing components — each handles its own role gating.
import UpdatesNoticesPanel from '../updates/UpdatesNoticesPanel';
import { SupervisorRegionalAssignmentCard } from '../staff/SupervisorRegionalAssignmentCard';
import { HierarchyAssignments } from '../staff/HierarchyAssignments';
import { RegionalAssignmentCards } from '../staff/RegionalAssignmentCards';
import { ServiceHistorySummary } from '../staff/ServiceHistorySummary';
import StaffChatTab from '../staff/StaffChatTab';
import CommercialCleanersBoard from './CommercialCleanersBoard';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';

function SectionHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4" style={{ color: GOLD }} />
      <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: GOLD }}>
        {label}
      </p>
    </div>
  );
}

export function ManagerReadOnlyMenu({ user }) {
  const location = useLocation();
  const [active, setActive] = useState('team');
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  // Iter-143 · FIX 4 — honour ?tab= deep-links.
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const t = params.get('tab');
    if (t && ['team', 'fleet', 'cleaners', 'notices', 'messages'].includes(t)) setActive(t);
  }, [location.search]);

  const sendChat = async () => {
    if (!newMessage.trim()) return;
    try {
      const r = await fetch(`${API}/api/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: newMessage.trim() }),
      });
      if (r.ok) {
        setChatMessages((p) => [...p, {
          content: newMessage.trim(),
          sender_id: user?.user_id,
          created_at: new Date().toISOString(),
        }]);
        setNewMessage('');
      }
    } catch { /* noop */ }
  };

  const bottomNav = (
    <>
      <BottomNavButton icon={Users}         label="Team"      active={active === 'team'}      onClick={() => setActive('team')}      testId="staff-app-rm-nav-team" />
      <BottomNavButton icon={Truck}         label="Fleet"     active={active === 'fleet'}     onClick={() => setActive('fleet')}     testId="staff-app-rm-nav-fleet" />
      <BottomNavButton icon={Sparkles}      label="Cleaners"  active={active === 'cleaners'}  onClick={() => setActive('cleaners')}  testId="staff-app-rm-nav-cleaners" />
      <BottomNavButton icon={Bell}          label="Notices"   active={active === 'notices'}   onClick={() => setActive('notices')}   testId="staff-app-rm-nav-notices" />
      <BottomNavButton icon={MessageSquare} label="Messages"  active={active === 'messages'}  onClick={() => setActive('messages')}  testId="staff-app-rm-nav-messages" />
    </>
  );

  return (
    <MobileShell
      title="Regional Manager · Oversight"
      subtitle={user?.name || user?.email}
      bottomNav={bottomNav}
      testId="staff-app-rm"
    >
      <div className="flex flex-wrap gap-4 py-2"><a href="/shift-briefings" className="text-sm text-[#C9A962] underline" data-testid="manager-briefings-link">NECS Standard briefings</a><a href="/closeout-attention" className="text-sm text-amber-300 underline" data-testid="manager-attention-link">Overdue & returned work</a></div>
      <div className="flex flex-wrap gap-4 py-2"><a href="/operation-records" className="text-sm text-[#C9A962] underline" data-testid="manager-closeouts-link">Property operation records</a><a href="/staff-rewards" className="text-sm text-[#C9A962] underline" data-testid="manager-rewards-link">Monthly progress & rewards</a></div>
      {active === 'team' && (
        <>
          <section data-testid="staff-app-rm-team">
            <SectionHeader icon={Users} label="Team Assignments · Read-only" />
            <SupervisorRegionalAssignmentCard user={user} />
          </section>
          <section>
            <SectionHeader icon={Users} label="Regional Assignment Cards" />
            <RegionalAssignmentCards user={user} />
          </section>
          <section>
            <SectionHeader icon={Users} label="Hierarchy Board" />
            <HierarchyAssignments user={user} />
          </section>
        </>
      )}

      {active === 'fleet' && (
        <section data-testid="staff-app-rm-fleet">
          <SectionHeader icon={Truck} label="Fleet Status · Read-only" />
          {/* Fleet vehicle cards come from the same admin endpoint the
              web dashboard uses. Kept inline to avoid rebuilding the
              vehicle grid. */}
          <ManagerFleetCards />
        </section>
      )}

      {active === 'cleaners' && (
        <section data-testid="staff-app-rm-cleaners">
          <SectionHeader icon={Sparkles} label="Commercial Cleaners HQ · Read-only" />
          <CommercialCleanersBoard testId="staff-app-rm-cleaners-board" />
        </section>
      )}

      {active === 'history' && (
        <section data-testid="staff-app-rm-history">
          <SectionHeader icon={Sparkles} label="Service History Summary" />
          <ServiceHistorySummary user={user} />
        </section>
      )}

      {active === 'notices' && (
        <>
          <section data-testid="staff-app-rm-notices">
            <SectionHeader icon={Bell} label="Updates & Notices" />
            <UpdatesNoticesPanel testIdPrefix="staff-app-rm-updates" />
          </section>
          <section>
            <SectionHeader icon={Sparkles} label="Service History Summary" />
            <ServiceHistorySummary user={user} />
          </section>
        </>
      )}

      {active === 'messages' && (
        <section data-testid="staff-app-rm-messages">
          <SectionHeader icon={MessageSquare} label="Messages" />
          <StaffChatTab
            user={user}
            chatMessages={chatMessages}
            newMessage={newMessage}
            setNewMessage={setNewMessage}
            onSendMessage={sendChat}
          />
        </section>
      )}
    </MobileShell>
  );
}

function ManagerFleetCards() {
  const [vehicles, setVehicles] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Lazy fetch on mount.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/company/vehicles`, { credentials: 'include' });
        if (r.ok) setVehicles(await r.json());
        else setVehicles([]);
      } catch { setVehicles([]); }
    })();
  }, []);

  if (vehicles === null) {
    return <p className="text-xs text-zinc-500">Loading fleet…</p>;
  }
  if (!vehicles.length) {
    return <p className="text-xs text-zinc-500 text-center py-6">No vehicles found for your region.</p>;
  }
  return (
    <div className="space-y-2" data-testid="staff-app-rm-fleet-list">
      {vehicles.map((v) => {
        const isOpen = expandedId === (v.vehicle_id || v.id);
        return (
          <button
            key={v.vehicle_id || v.id}
            type="button"
            onClick={() => setExpandedId(isOpen ? null : (v.vehicle_id || v.id))}
            className="w-full text-left rounded-xl border border-zinc-700 bg-zinc-900/70 p-3 min-h-[64px]"
            data-testid={`staff-app-rm-fleet-card-${v.vehicle_id || v.id}`}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg grid place-items-center bg-zinc-800">
                <Truck className="h-5 w-5" style={{ color: GOLD }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{v.name || v.plate || v.vehicle_id}</p>
                <p className="text-[10px] text-zinc-400 truncate">
                  {v.assigned_to_name || 'Unassigned'} · {v.status || '—'}
                </p>
              </div>
              <span className="text-[9px] uppercase tracking-widest text-zinc-500">
                {isOpen ? 'Hide' : 'Details'}
              </span>
            </div>
            {isOpen && (
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-zinc-300">
                <Kv k="Plate" v={v.plate} />
                <Kv k="Make/Model" v={[v.make, v.model].filter(Boolean).join(' ')} />
                <Kv k="Year" v={v.year} />
                <Kv k="Region" v={v.region || v.region_code} />
                <Kv k="Odometer" v={v.odometer} />
                <Kv k="Last check" v={v.last_safety_check_at ? new Date(v.last_safety_check_at).toLocaleDateString() : '—'} />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div className="rounded bg-zinc-950/60 border border-zinc-800 p-2">
      <p className="text-[9px] uppercase tracking-widest text-zinc-500">{k}</p>
      <p className="text-white truncate">{v || '—'}</p>
    </div>
  );
}

export default ManagerReadOnlyMenu;
